import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime, formatNumber } from '../ui/format';
import { Badge, Loading, Panel } from '../ui/Page';
import { uploadFile } from '../files/api';
import {
  IMPORT_FIELDS,
  getImport,
  isRunning,
  listImports,
  previewImport,
  startImport,
  type DuplicateStrategy,
  type ImportEntity,
  type ImportJob,
  type ImportPreview,
  type Mapping,
} from './imports-api';
import { useRealtimeTopic } from '../app/realtime-store';

/**
 * Bringing a spreadsheet in.
 *
 * Three steps, in the order every tool worth using does them: choose the file, agree what the
 * columns mean, run it. The middle step shows the first rows beside each dropdown, because a mapping
 * screen with no sample data is a screen where somebody picks "telefone" for a column of postcodes
 * and finds out four thousand rows later.
 *
 * The file goes through the media library, which already checks the type, the extension, the magic
 * bytes and the quota. The import reads it from there; nothing is uploaded twice.
 */

type Step = 'file' | 'map' | 'running';

const ENTITIES: ImportEntity[] = ['LEAD', 'COMPANY', 'CONTACT'];

export function ImportWizard(): React.JSX.Element {
  const client = useQueryClient();
  const picker = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('file');
  const [entity, setEntity] = useState<ImportEntity>('LEAD');
  const [fileId, setFileId] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [mapping, setMapping] = useState<Mapping>({});
  const [duplicates, setDuplicates] = useState<DuplicateStrategy>('SKIP');
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const history = useQuery({ queryKey: ['imports'], queryFn: listImports });

  /*
   * Pushed while it runs, with a slow floor, and nothing once it is done.
   *
   * The runner announces each batch of a hundred rows (Phase 80), which is what moves the bar. The
   * two-second poll it replaces was the most expensive kind: fast enough to cost real queries, slow
   * enough to feel like waiting, and running hardest exactly while the import was working.
   */
  const job = useQuery({
    queryKey: ['import', jobId],
    queryFn: () => getImport(jobId!),
    enabled: Boolean(jobId),
    refetchInterval: (query) => (query.state.data && isRunning(query.state.data) ? 20_000 : false),
  });

  useRealtimeTopic(jobId ? 'import' : null, () => {
    void client.invalidateQueries({ queryKey: ['import', jobId] });
    void client.invalidateQueries({ queryKey: ['imports'] });
  });

  const send = useMutation({
    mutationFn: async (file: File) => {
      const stored = await uploadFile(file);

      return { stored, preview: await previewImport({ fileId: stored.id, entityType: entity }) };
    },
    onSuccess: ({ stored, preview: read }) => {
      setFileId(stored.id);
      setFileName(stored.name);
      setPreview(read);
      setMapping(read.mapping);
      setStep('map');
      setError(null);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const start = useMutation({
    mutationFn: () => startImport({ fileId: fileId!, entityType: entity, mapping, duplicates }),
    onSuccess: (created) => {
      setJobId(created.id);
      setStep('running');
      setError(null);
      void client.invalidateQueries({ queryKey: ['imports'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const mapped = Object.values(mapping).filter((field) => field !== null).length;

  function reset(): void {
    setStep('file');
    setFileId(null);
    setFileName('');
    setPreview(null);
    setMapping({});
    setJobId(null);
    setError(null);
  }

  return (
    <div className="stack">
      <nav className="steps" aria-label={t('import.title')}>
        {(['file', 'map', 'running'] as Step[]).map((entry, index) => (
          <span
            key={entry}
            className={`step${step === entry ? ' step--current' : ''}${
              (['file', 'map', 'running'] as Step[]).indexOf(step) > index ? ' step--done' : ''
            }`}
          >
            {index + 1}. {t(`import.step.${entry}`)}
          </span>
        ))}
      </nav>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {step === 'file' && (
        <Panel title={t('import.chooseFile')}>
          <div className="form-grid">
            <label className="field">
              <span>{t('import.what')}</span>
              <select
                className="select"
                value={entity}
                onChange={(event) => setEntity(event.target.value as ImportEntity)}
              >
                {ENTITIES.map((each) => (
                  <option key={each} value={each}>
                    {t(`import.entity.${each}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <p className="muted small">{t('import.fileHint')}</p>

          <input
            ref={picker}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];

              if (file) send.mutate(file);
              event.target.value = '';
            }}
          />

          <button
            type="button"
            className="button button--primary"
            disabled={send.isPending}
            onClick={() => picker.current?.click()}
          >
            {send.isPending ? t('import.reading') : t('import.chooseFile')}
          </button>
        </Panel>
      )}

      {step === 'map' && preview && (
        <Panel title={t('import.mapColumns')}>
          <p className="muted small">
            {fileName} · {t('import.rowsFound').replace('{n}', formatNumber(preview.totalRows))}
          </p>

          <div className="mapping">
            {preview.headers.map((header) => (
              <div key={header} className="mapping__row">
                <span className="cell-stack">
                  <span className="strong">{header}</span>
                  {/* The first rows beside the dropdown: what makes the choice checkable. */}
                  <span className="muted small">
                    {preview.rows
                      .slice(0, 3)
                      .map((row) => row[preview.headers.indexOf(header)] ?? '')
                      .filter(Boolean)
                      .join(' · ') || t('import.emptyColumn')}
                  </span>
                </span>

                <select
                  className="select"
                  value={mapping[header] ?? ''}
                  onChange={(event) =>
                    setMapping({ ...mapping, [header]: event.target.value || null })
                  }
                >
                  <option value="">{t('import.ignore')}</option>
                  {IMPORT_FIELDS[entity].map((field) => (
                    <option key={field} value={field}>
                      {t(`record.field.${field}`)}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <label className="field">
            <span>{t('import.duplicates')}</span>
            <select
              className="select"
              value={duplicates}
              onChange={(event) => setDuplicates(event.target.value as DuplicateStrategy)}
            >
              <option value="SKIP">{t('import.duplicates.SKIP')}</option>
              <option value="UPDATE">{t('import.duplicates.UPDATE')}</option>
              <option value="CREATE">{t('import.duplicates.CREATE')}</option>
            </select>
            <span className="muted small">{t(`import.duplicates.${duplicates}.hint`)}</span>
          </label>

          <div className="cell-inline">
            <button
              type="button"
              className="button button--primary"
              disabled={mapped === 0 || start.isPending}
              onClick={() => start.mutate()}
            >
              {start.isPending
                ? t('work.creating')
                : t('import.run').replace('{n}', formatNumber(preview.totalRows))}
            </button>

            <button type="button" className="button button--quiet button--compact" onClick={reset}>
              {t('confirm.cancel')}
            </button>

            {mapped === 0 && <span className="muted small">{t('import.mapSomething')}</span>}
          </div>
        </Panel>
      )}

      {step === 'running' && (
        <Panel title={t('import.progress')}>
          {job.isLoading || !job.data ? (
            <Loading />
          ) : (
            <>
              <Result job={job.data} />

              {!isRunning(job.data) && (
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={reset}
                >
                  {t('import.another')}
                </button>
              )}
            </>
          )}
        </Panel>
      )}

      <Panel title={t('import.history')}>
        {(history.data?.imports.length ?? 0) === 0 ? (
          <p className="muted">{t('import.noHistory')}</p>
        ) : (
          <ul className="home-list">
            {(history.data?.imports ?? []).map((entry) => (
              <li key={entry.id}>
                <span className="cell-stack">
                  <span className="cell-inline">
                    <Badge tone={toneOf(entry.status)}>{t(`import.status.${entry.status}`)}</Badge>
                    <span>{t(`import.entity.${entry.entity_type}`)}</span>
                    <span className="muted small">{formatDateTime(entry.created_at)}</span>
                  </span>
                  <span className="muted small">
                    {t('import.summary')
                      .replace('{imported}', formatNumber(entry.imported))
                      .replace('{updated}', formatNumber(entry.updated))
                      .replace('{skipped}', formatNumber(entry.skipped))
                      .replace('{failed}', formatNumber(entry.failed))}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function Result({ job }: { job: ImportJob }): React.JSX.Element {
  const done =
    job.total_rows > 0
      ? (job.imported + job.updated + job.skipped + job.failed) / job.total_rows
      : 0;

  return (
    <div className="stack">
      <div className="quota__bar" role="progressbar" aria-valuenow={Math.round(done * 100)}>
        <span style={{ width: `${Math.min(done, 1) * 100}%` }} />
      </div>

      <div className="metrics">
        <Figure label={t('import.imported')} value={formatNumber(job.imported)} />
        <Figure label={t('import.updated')} value={formatNumber(job.updated)} />
        <Figure label={t('import.skipped')} value={formatNumber(job.skipped)} />
        <Figure label={t('import.failed')} value={formatNumber(job.failed)} />
      </div>

      {job.failure_reason && (
        <p className="alert" role="alert">
          {job.failure_reason}
        </p>
      )}

      {job.errors.length > 0 && (
        <>
          <h3 className="record__subhead">{t('import.whatFailed')}</h3>
          <ul className="home-list">
            {job.errors.map((entry, index) => (
              <li key={`${entry.row}-${index}`}>
                <span className="muted small">
                  {entry.row > 0 ? `${t('import.line')} ${entry.row}: ` : ''}
                  {entry.reason}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="metric">
      <p className="metric__label">{label}</p>
      <p className="metric__value">{value}</p>
    </div>
  );
}

function toneOf(status: string): 'positive' | 'danger' | 'info' | 'neutral' {
  if (status === 'DONE') return 'positive';
  if (status === 'FAILED') return 'danger';
  if (status === 'RUNNING' || status === 'PENDING') return 'info';

  return 'neutral';
}
